import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { focusElement } from 'platform/utilities/ui';

import {
  buildDateFormatter,
  claimAvailable,
  getFailedSubmissionsWithinLast30Days,
  generateClaimTitle,
  isChampvaProviderClaim,
  withClaimStatusMetaIfEnabled,
} from '~/platform/shared/claims-appeals';
import { setFocus, focusNotificationAlert } from '../utils/page';
import NeedHelp from './NeedHelp';
import ClaimsBreadcrumbs from './ClaimsBreadcrumbs';
import ServiceUnavailableAlert from './ServiceUnavailableAlert';
import ClaimContentionList from './ClaimContentionList';
import Notification from './Notification';
import TabbedDetail from './TabbedDetail';
import TabNav from './TabNav';
import Type1UnknownUploadError from './Type1UnknownUploadError';
import UploadType2ErrorAlert from './UploadType2ErrorAlert';

const focusHeader = () => {
  setFocus('.claim-contentions-header');
};

export default function ClaimDetailLayout(props) {
  const {
    activeTabId,
    claim,
    clearNotification,
    currentTab,
    loading,
    message,
    onTabChange,
    tabPanels,
    tabsLabel,
  } = props;

  const type1UnknownErrors = useSelector(
    state => state.disability.status.notifications.type1UnknownErrors,
  );
  const cstChampvaCustomContentEnabled = useSelector(
    state => state.featureToggles?.cst_champva_custom_content || false,
  );
  const displayClaim = withClaimStatusMetaIfEnabled(
    claim,
    cstChampvaCustomContentEnabled,
  );

  const failedSubmissionsWithinLast30Days = useMemo(
    () =>
      getFailedSubmissionsWithinLast30Days(
        claim?.attributes?.evidenceSubmissions,
      ),
    [claim?.attributes?.evidenceSubmissions],
  );

  const tabs = ['Status', 'Files', 'Details', 'Overview'];

  // Providing an empty array will show the breadcrumbs for the main claims
  //   list page while the detail page loads (to avoid a flash of incorrect
  //   content).
  let breadcrumbs = [];
  let bodyContent;
  let headingContent;

  if (loading) {
    bodyContent = (
      <va-loading-indicator
        set-focus
        message="Loading your claim information..."
      />
    );
  } else if (claimAvailable(displayClaim)) {
    breadcrumbs = [
      {
        href: '../status',
        label: generateClaimTitle(displayClaim, 'breadcrumb', currentTab),
        isRouterLink: true,
      },
    ];
    const claimTitle = generateClaimTitle(displayClaim, 'detail');
    const { claimDate, contentions } = displayClaim.attributes || {};
    const detailMeta = displayClaim.attributes?.claimStatusMeta?.detail || {};
    const detailPageTitle = detailMeta.pageTitle || claimTitle;
    const detailSectionTitle =
      detailMeta.sectionTitle ?? 'What you\u2019ve claimed';
    const detailSectionGroups = detailMeta.sectionGroups || [];
    const hasContentions = contentions?.length > 0;
    const showClaimInfoSection =
      detailSectionGroups.length > 0 || hasContentions;

    const formattedClaimDate = buildDateFormatter()(claimDate);
    const claimSubheader = `Received on ${formattedClaimDate}`;

    headingContent = (
      <>
        <h1 className="claim-title">
          {detailPageTitle}
          <span className="vads-u-font-family--sans vads-u-margin-top--1">
            {claimSubheader}
          </span>
        </h1>

        {message &&
          !(type1UnknownErrors && type1UnknownErrors.length > 0) &&
          (currentTab === 'Files' || currentTab === 'Status') && (
            <div className="vads-u-margin-top--5">
              <Notification
                title={message.title}
                body={message.body}
                type={message.type}
                // CHAMPVA success titles name the applicant ("We received a
                // marriage certificate for John"), so they stay masked in
                // Datadog RUM the way they were in AdditionalEvidencePage.
                // Keyed on the provider alone, not cstChampvaCustomContentEnabled:
                // buildUploadNotification adds the name from the provider too, so
                // the name is present even when that flag is off.
                maskTitle={
                  message.type === 'error' ||
                  isChampvaProviderClaim(displayClaim)
                }
                onClose={() => {
                  focusElement('.claim-title');
                  clearNotification();
                }}
                onSetFocus={focusNotificationAlert}
              />
            </div>
          )}

        {type1UnknownErrors &&
          type1UnknownErrors.length > 0 &&
          (currentTab === 'Files' || currentTab === 'Status') && (
            <div className="vads-u-margin-top--5">
              <Notification
                title={
                  cstChampvaCustomContentEnabled &&
                  isChampvaProviderClaim(displayClaim)
                    ? 'We need you to submit files by mail or fax'
                    : 'We need you to submit files by mail or in person'
                }
                body={
                  <Type1UnknownUploadError
                    errorFiles={type1UnknownErrors}
                    isChampvaFlow={
                      cstChampvaCustomContentEnabled &&
                      isChampvaProviderClaim(displayClaim)
                    }
                    useVaTabs={Boolean(tabPanels)}
                  />
                }
                role="alert"
                type="error"
                onSetFocus={focusNotificationAlert}
              />
            </div>
          )}

        {failedSubmissionsWithinLast30Days.length > 0 &&
          (currentTab === 'Files' || currentTab === 'Status') && (
            <UploadType2ErrorAlert
              failedSubmissions={failedSubmissionsWithinLast30Days}
              isStatusPage={currentTab === 'Status'}
              trackedItems={claim?.attributes?.trackedItems}
            />
          )}
        {showClaimInfoSection && (
          <div className="claim-contentions">
            {detailSectionTitle && (
              <h2 className="claim-contentions-header vads-u-font-size--h3">
                {detailSectionTitle}
              </h2>
            )}
            {detailSectionGroups.length > 0 ? (
              detailSectionGroups.map((group, groupIndex) => {
                // When there is no sectionTitle h2, promote group headings to h2
                // to avoid skipping heading levels (h1 → h3)
                const GroupHeading = detailSectionTitle ? 'h3' : 'h2';
                return (
                  <div key={group.title} className="vads-u-margin-bottom--2">
                    <GroupHeading
                      className={`vads-u-font-size--h3 vads-u-margin-bottom--1 ${
                        groupIndex === 0
                          ? 'vads-u-margin-top--0'
                          : 'vads-u-margin-top--1'
                      }`}
                    >
                      {group.title}
                    </GroupHeading>
                    <ul>
                      {(group.items || []).map((item, index) => (
                        <li
                          key={`${group.title}-${index}`}
                          data-dd-privacy="mask"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            ) : (
              <ClaimContentionList
                contentions={contentions}
                onClick={focusHeader}
              />
            )}
          </div>
        )}
      </>
    );

    bodyContent = (
      <div className="claim-container">
        {tabPanels ? (
          <TabbedDetail
            label={tabsLabel}
            activeTabId={activeTabId}
            tabs={tabPanels}
            onTabChange={onTabChange}
          />
        ) : (
          <>
            <TabNav id={displayClaim.id} />
            {tabs.map(tab => (
              <div key={tab} id={`tabPanel${tab}`} className="tab-panel">
                {currentTab === tab && (
                  <div className="tab-content claim-tab-content">
                    {props.children}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    );
  } else {
    // Will provide a default title, e.g. "Status of your claim"
    breadcrumbs = [
      {
        href: '../status',
        label: generateClaimTitle(null, 'breadcrumb', currentTab),
        isRouterLink: true,
      },
    ];
    bodyContent = (
      <>
        <h1>We encountered a problem</h1>
        <ServiceUnavailableAlert
          headerLevel={2}
          services={['claims']}
          useSingular
        />
      </>
    );
  }

  return (
    <div>
      <div name="topScrollElement" />
      <div className="row">
        <div className="usa-width-two-thirds medium-8 columns">
          <ClaimsBreadcrumbs crumbs={breadcrumbs} />
          {!!headingContent && <div>{headingContent}</div>}
          <div>{bodyContent}</div>
          <NeedHelp claim={displayClaim} />
        </div>
      </div>
    </div>
  );
}

ClaimDetailLayout.propTypes = {
  activeTabId: PropTypes.string,
  children: PropTypes.any,
  claim: PropTypes.object,
  clearNotification: PropTypes.func,
  currentTab: PropTypes.string,
  loading: PropTypes.bool,
  message: PropTypes.object,
  tabPanels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    }),
  ),
  tabsLabel: PropTypes.string,
  onTabChange: PropTypes.func,
};
